import React, { useEffect, useState, useRef } from "react";
import VisibilitySensor from "react-visibility-sensor";
import { SegmentsLayoutExpandableGrid } from "../LabSoftware/IntegratedSegmentsLayouts";
import "../LabSoftware/IntegratedSegmentsLayouts.css";
import "../PharmacySoftware/MapsFeaturesSection.css";
import "./PharmacySoftware.css";
import Newsletter from "../Components/Newsletter";
import Footer from "../Components/Footer";
import Header from "../Components/Header";
import Faqs from "../PharmacySoftware/Faqs";
import Testimonial from "../PharmacySoftware/Testimonial";
import ScreenCarousal from "../PharmacySoftware/ScreenCarousal";
import MapsBannerSlider from "../PharmacySoftware/MapsBannerSlider";
import SearchEngineMagnifyingGlass from "../PharmacySoftware/SearchEngineMagnifyingGlass";
import { MapsIntroLayoutSelector } from "../PharmacySoftware/MapsIntroLayouts";
import PricingTableSecond from "../Components/PricingTableSecond";
import { GoChecklist } from "react-icons/go";
import PricingPlans from "./PricingPlans";

const PHARMACY_PROGRAMS_DATA = [
  {
    id: "general",
    title: "General Pharmacy",
    desc: "It is just not the point of sale (POS) software, but also covers all aspects of regulatory, inventory, financial, supply chain, sales, cashflow and profitability management.",
  },
  {
    id: "hospital",
    title: "Hospital Pharmacy",
    desc: "In addition to all mentioned features of general pharmacy, it integrates pharmacy operations with doctors' prescriptions in all associated hospital departments.",
  },
  {
    id: "plus",
    title: "Pharmacy Plus",
    desc: "It is an upgraded form of pharmacy software that seamlessly integrates employee attendance and payroll management to streamline day-to-day operations.",
  },
  {
    id: "erp",
    title: "Pharmacy ERP",
    desc: "It is a complete solution for any pharmacy outlet, providing dedicated dashboards for every department with an integrated and robust reporting system.",
  },
];

const MAPS_FEATURES_DATA = [
  { id: "regulatory", title: "Regulatory Compliance", desc: "It includes SOPs and regulatory reports for full compliance with industry standards. It also has a dedicated segment for handling controlled drugs.", icon: "/assets/img/icon/RegulatoryCompliance.png" },
  { id: "inventory", title: "Inventory Control", desc: "Efficiently oversee and manage real-time inventory levels, minimizing waste and ensuring essential medicines remain consistently in stock.", icon: "/assets/img/icon/InventoryControl.png" },
  { id: "useraccess", title: "User Access Management", desc: "A dedicated section manages access levels and restrictions for different users, aligning with business administrative objectives.", icon: "/assets/img/icon/UserAcessManagement.png" },
  { id: "layout", title: "Layout Management", desc: "Manage and track stock locations and responsibilities by classifying storage positions. This ensures better control over physical stock and staff duties.", icon: "/assets/img/icon/LayoutManagement.png" },
  { id: "sales", title: "Sales Management", desc: "Monitor sales data and key performance metrics to identify emerging trends and sales strategies, and boost overall customer satisfaction.", icon: "/assets/img/icon/SalesManagement.png" },
  { id: "customer", title: "Customer Management", desc: "Maintain and manage customer information, track interactions, and improve customer service through effective CRM tools.", icon: "/assets/img/icon/CustomerManagement.png" },
  { id: "supplychain", title: "Supply Chain Management", desc: "Address inventory management, order processing, and supplier coordination simultaneously for a smooth and efficient supply chain.", icon: "/assets/img/icon/SupplyChainManagement.png" },
  { id: "financial", title: "Financial Management", desc: "Manage billing, invoicing, and cash flow management to maintain profitability and ensure the financial stability of the pharmacy.", icon: "/assets/img/icon/FinancialManagement.png" },
  { id: "autoalerts", title: "Auto Alerts", desc: "Prevent stock shortages by notifying when products are below threshold levels and alerting for items nearing expiry.", icon: "/assets/img/icon/AutoAlerts.png" },
];

const MAPS_FEATURES_SEGMENTS = MAPS_FEATURES_DATA.map(({ id, title, icon, desc }) => ({
  id,
  title: title.toUpperCase(),
  icon,
  description: desc,
}));

const BUSINESS_CAPABILITIES_DATA = [
  { id: "profiling", title: "Product Profiling", desc: "Complete product details including product sale history, product price changes over time, in/out ratio etc." },
  { id: "requisitions", title: "Automated Requisitions", desc: "Automated requests for stock are generated on the basis of user defined values, system calculated values or both. In this way stock ordering process became much simpler and worry-free." },
  { id: "patient", title: "Patient Records", desc: "It can manage each patient billing history and sale records to provide better care to customers." },
  { id: "doctor", title: "Doctor Panel", desc: "It can track business generated by prescribing doctors. Pharmacy user can create bills according to the prescriptions generated by doctors through doctors' panel." },
];

function PharmacyProgramsSection({ accentInView = false }) {
  const data = PHARMACY_PROGRAMS_DATA;
  const inViewClass = accentInView ? "pharmacy-programs-accent-in-view" : "";
  return (
    <div className="pharmacy-programs pharmacy-programs-accent">
      <div className={`pharmacy-programs-accent-grid pharmacy-programs-accent-anim-stagger pharmacy-programs-accent-hover-scaleLift ${inViewClass}`}>
        {data.map((item, idx) => (
          <div key={item.id} className="pharmacy-programs-accent-card">
            <div className="pharmacy-programs-accent-card-inner" data-accent-index={idx}>
              <div className="pharmacy-programs-accent-bar" />
              <h5>{item.title}</h5>
              <p>{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BusinessCentricSection() {
  return (
    <section className="position-relative feature-section ptb-100 gray-light-bg pharmacy-bc-section">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-10 mb-4 pharmacy-section-heading-center">
            <h2 className="web mb-3">Business-Centric Capabilities</h2>
            <p>Business-focused tools designed to enhance operational efficiency, automate stock processes, and provide deeper financial and patient insights for smarter pharmacy management.</p>
          </div>
        </div>
        <div className="pharmacy-bc-numbered">
          {BUSINESS_CAPABILITIES_DATA.map((item) => (
            <div key={item.id} className="pharmacy-bc-numbered-card">
              <div className="pharmacy-bc-numbered-bar" />
              <div className="pharmacy-bc-numbered-content">
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PharmacySoftware() {
  const [accentInView, setAccentInView] = useState(false);
  const [mapsFeaturesInView, setMapsFeaturesInView] = useState(false);
  const accentWasVisibleRef = useRef(false);
  const mapsFeaturesWasVisibleRef = useRef(false);

  const handleMapsFeaturesVisibility = (visible) => {
    if (visible && !mapsFeaturesWasVisibleRef.current) {
      setMapsFeaturesInView(true);
      mapsFeaturesWasVisibleRef.current = true;
    }
  };

  const handleAccentVisibility = (visible) => {
    if (visible && !accentWasVisibleRef.current) {
      setAccentInView(true);
      accentWasVisibleRef.current = true;
    }
  };

  var BreadCrumbTitle = "MAPS :: D-Right Technologies";
  document.title = BreadCrumbTitle;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <div className="main">
        <MapsBannerSlider />
        <MapsIntroLayoutSelector />

        {/* Complete Range of Pharmacy Software Programs
        <section className="promo-section ptb-100 position-relative overflow-hidden gray-light-bg">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-9 col-lg-10">
                <div className="section-heading text-center mb-5"> 
                  <h2 className="apt">
                    Complete Range of Pharmacy Software Programs
                  </h2>
                  <p className="text-muted para-desc mb-0 mx-auto">
                    We have a complete range of pharmacy software programs. We
                    offer a range of pharmacy software programs as per the
                    varying needs for different types and sizes of pharmacy
                    outlets.
                  </p>
                </div>
              </div>
            </div>
            <VisibilitySensor onChange={handleAccentVisibility} partialVisibility offset={{ bottom: 80 }}>
              <div>
                <PharmacyProgramsSection accentInView={accentInView} />
              </div>
            </VisibilitySensor>
          </div>
        </section>
        */}

        <section id="features" className="feature-section ptb-100 gray-light-bg integrated-segments-section maps-features-section">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-10 mb-4 pharmacy-section-heading-center">
                <h2 className="web mb-3">Complete Workflow Coverage</h2>
                <p>ClinTa MAPS offers a comprehensive suite of features designed to streamline pharmacy management, ensuring compliance, efficient layout management, and robust supply chain oversight.</p>
              </div>
            </div>
            <VisibilitySensor onChange={handleMapsFeaturesVisibility} partialVisibility>
              <div className="row">
                <div className="col-12">
                  <SegmentsLayoutExpandableGrid
                    segments={MAPS_FEATURES_SEGMENTS}
                    animationMode="stagger"
                    hoverMode="split"
                    inView={mapsFeaturesInView}
                  />
                </div>
              </div>
            </VisibilitySensor>
          </div>
        </section>

        <BusinessCentricSection />

        {/* <section id="about" className="about-new-section ptb-100 specialized-search-section">
            <div className="container">
                <div className="search-engine-section">
                    <div className="search-engine-magnifying-glass-wrap">
                        <SearchEngineMagnifyingGlass />
                    </div>
                    <div className="search-engine-content-wrap">
                        <h2>Specialized Search Engine</h2>
                        <p>A tool designed for creative decision-making, enabling businesses to explore new avenues for growth with deep insights into specific market segments.</p>
                        <ul className="search-engine-points">
                            <li>Search by <strong>brand</strong>, <strong>category</strong>, <strong>molecule</strong>, <strong>class</strong>, or <strong>company</strong></li>
                            <li>Search by <strong>location</strong> or <strong>position</strong></li>
                            <li>Data on highest and lowest unit or value-selling brands and molecules</li>
                            <li>Identify trends, top performers, and underperforming areas for market success</li>
                        </ul>
                    </div>
                </div>
            </div>
        </section> */}

        {/* <section
          id="about"
          className="about-us ptb-100 position-relative gray-light-bg"
        >
          <div className="container">
            <div className="row align-items-center justify-content-lg-between justify-content-md-center">
              <div className="col-md-5 col-lg-4">
                <div className="about-content-right">
                  <img
                    // src="/assets/img/app-mobile-image-2.png"
                    src="/assets/img/detect_undetectable_pharmacy.png"
                    alt="Detect_Pharmacy_Loss"
                    className="img-fluid"
                  />
                </div>
              </div>
              <div className="col-md-12 col-lg-7">
                <div className="about-content-left section-heading">
                  <h2 className="apt">Safeguard Undetectable Losses</h2>
                  <p>
                    Discover challenges of undetectable losses of pharmacy
                    operations and explore how our software solutions can help
                    you ensuring efficiency, compliance, and profitability in
                    your pharmacy operations
                  </p>
                  <ul className="check-list-wrap">
                    <li>
                      <strong>Inventory Shrinkage</strong> – It can be caused by
                      theft, pilferage, or improper inventory handling. Our
                      monitoring and controls don’t let losses go unnoticed.
                    </li>
                    <li>
                      <strong>Unrecorded Sales</strong> –It is a major
                      undetectable loss. Our accurate tracking mechanisms,
                      prevent unrecorded sales, and ultimately pharmacy revenue
                      loss.
                    </li>
                    <li>
                      <strong>Underutilization of Resources</strong> – Our
                      reporting system prevents losses from underutilized
                      resources through its monitoring and optimization
                      segments.
                    </li>
                    <li>
                      <strong>Purchasing Errors</strong> – Our integrated
                      procurement module prevents unnoticed financial losses of
                      purchasing errors, such as over & under-ordering.
                    </li>
                    <li>
                      <strong> Regulatory Non-Compliance Penalties</strong> –
                      Our proactive compliance monitoring system, prevents
                      regulatory non-compliance & its resultant penalties.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section> */}
        
        {/* <section className="position-relative overflow-hidden ptb-100">
                    <div className="mask-65"></div>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-md-9 col-lg-8">
                                <div className="section-heading text-center text-white">
                                    <h2 className="text-white">The Most Uses App Platform</h2>
                                    <p>
                                        Start working with that can provide everything you need to
                                        generate awareness, drive traffic, connect. Efficiently
                                        transform granular value with client-focused content.
                                    </p>
                                </div>
                                <div className="video-promo-content my-5 pb-4">
                                    <a
                                        href="https://www.youtube.com/watch?v=9No-FiEInLA"
                                        className="popup-youtube video-play-icon text-center m-auto"
                                    >
                                        <span className="ti-control-play"></span>{" "}
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="row justify-content-md-center justify-content-sm-center">
                            <div className="col-sm-6 col-md-6 col-lg-4">
                                <div className="bg-white p-5 rounded text-center shadow mt-lg-0 mt-4">
                                    <div className="icon-text-wrap">
                                        <i className="fab fa-apple icon-size-md color-primary mb-2"></i>
                                        <h5>Apple Store</h5>
                                    </div>
                                    <div className="p-20px">
                                        <p className="m-0px">
                                            Seamlessly foster fully researched convergence and
                                            interactive resources administrate data.
                                        </p>
                                        <a className="btn btn-brand-02 btn-sm btn-rounded" href="#">
                                            Download Now
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-6 col-md-6 col-lg-4">
                                <div className="bg-white p-5 rounded text-center shadow mt-lg-0 mt-4">
                                    <div className="icon-text-wrap">
                                        <i className="fab fa-google-play icon-size-md color-primary mb-2"></i>
                                        <h5>Google Play</h5>
                                    </div>
                                    <div className="p-20px">
                                        <p className="m-0px">
                                            Assertively build B2C manufactured products before
                                            front-end optimize frictionless communities tailers.
                                        </p>
                                        <a className="btn btn-brand-02 btn-sm btn-rounded" href="#">
                                            Download Now
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-6 col-md-6 col-lg-4">
                                <div className="bg-white p-5 rounded text-center shadow mt-lg-0 mt-4">
                                    <div className="icon-text-wrap">
                                        <i className="fab fa-windows icon-size-md color-primary mb-2"></i>
                                        <h5>Microsoft Store</h5>
                                    </div>
                                    <div className="p-20px">
                                        <p className="m-0px">
                                            Completely engineer pandemic human capital via
                                            reconceptualize installed base niche markets.
                                        </p>
                                        <a className="btn btn-brand-02 btn-sm btn-rounded" href="#">
                                            Download Now
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section> */}
        {/* <ScreenCarousal/> */}


        {/* <PricingTableSecond product="pharmacy"/> */}

        {/* <Testimonial/> */}

        {/* <Faqs/> */}

        {/* <section className="our-blog-section ptb-100 gray-light-bg">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-md-9 col-lg-8">
                                <div className="section-heading text-center">
                                    <h2>Our Latest News</h2>
                                    <p>
                                        Efficiently matrix robust total linkage after market
                                        positioning bandwidth. Holisticly restore B2B materials
                                        rather than brand flexible paradigms vis-a-vis
                                        mission-critical e-commerce.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="row justify-content-center">
                            <div className="col-md-6 col-lg-4">
                                <div className="single-blog-card card border-0 shadow-sm mt-4">
                                    <div className="blog-img position-relative">
                                        <img
                                            src="/assets/img/blog/1.jpg"
                                            className="card-img-top"
                                            alt="blog"
                                        />
                                        <div className="meta-date">
                                            <strong>24</strong>
                                            <small>Apr</small>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="post-meta mb-2">
                                            <ul className="list-inline meta-list">
                                                <li className="list-inline-item">
                                                    <i className="fas fa-heart mr-2"></i>
                                                    <span>45</span>
                                                    Comments
                                                </li>
                                                <li className="list-inline-item">
                                                    <i className="fas fa-share-alt mr-2"></i>
                                                    <span>10</span>
                                                    Share
                                                </li>
                                            </ul>
                                        </div>
                                        <h3 className="h5 mb-2 card-title">
                                            <a href="#">Appropriately productize fully</a>
                                        </h3>
                                        <p className="card-text">
                                            Some quick example text to build on the card title and
                                            make up the bulk.
                                        </p>
                                        <a href="#" className="detail-link">
                                            Read more <span className="ti-arrow-right"></span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <div className="single-blog-card card border-0 shadow-sm mt-4">
                                    <div className="blog-img position-relative">
                                        <img
                                            src="/assets/img/blog/2.jpg"
                                            className="card-img-top"
                                            alt="blog"
                                        />
                                        <div className="meta-date">
                                            <strong>24</strong>
                                            <small>Apr</small>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="post-meta mb-2">
                                            <ul className="list-inline meta-list">
                                                <li className="list-inline-item">
                                                    <i className="fas fa-heart mr-2"></i>
                                                    <span>45</span>
                                                    Comments
                                                </li>
                                                <li className="list-inline-item">
                                                    <i className="fas fa-share-alt mr-2"></i>
                                                    <span>10</span>
                                                    Share
                                                </li>
                                            </ul>
                                        </div>
                                        <h3 className="h5 mb-2 card-title">
                                            <a href="#">Quickly formulate backend</a>
                                        </h3>
                                        <p className="card-text">
                                            Synergistically engage effective ROI after customer
                                            directed partnerships.
                                        </p>
                                        <a href="#" className="detail-link">
                                            Read more <span className="ti-arrow-right"></span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <div className="single-blog-card card border-0 shadow-sm mt-4">
                                    <div className="blog-img position-relative">
                                        <img
                                            src="/assets/img/blog/3.jpg"
                                            className="card-img-top"
                                            alt="blog"
                                        />
                                        <div className="meta-date">
                                            <strong>24</strong>
                                            <small>Apr</small>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="post-meta mb-2">
                                            <ul className="list-inline meta-list">
                                                <li className="list-inline-item">
                                                    <i className="fas fa-heart mr-2"></i>
                                                    <span>45</span>
                                                    Comments
                                                </li>
                                                <li className="list-inline-item">
                                                    <i className="fas fa-share-alt mr-2"></i>
                                                    <span>10</span>
                                                    Share
                                                </li>
                                            </ul>
                                        </div>
                                        <h3 className="h5 mb-2 card-title">
                                            <a href="#">Objectively extend extensive</a>
                                        </h3>
                                        <p className="card-text">
                                            Holisticly mesh open-source leadership rather than
                                            proactive users.{" "}
                                        </p>
                                        <a href="#" className="detail-link">
                                            Read more <span className="ti-arrow-right"></span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section> */}
        {/* <section id="contact" className="contact-us-section ptb-100">
                    <div className="container">
                        <div className="row justify-content-around">
                            <div className="col-12 pb-3 message-box d-none">
                                <div className="alert alert-danger"></div>
                            </div>
                            <div className="col-md-12 col-lg-5 mb-5 mb-md-5 mb-sm-5 mb-lg-0">
                                <div className="contact-us-form gray-light-bg rounded p-5">
                                    <h4>Ready to get started?</h4>
                                    <form
                                        action="#"
                                        method="POST"
                                        id="contactForm"
                                        className="contact-us-form"
                                    >
                                        <div className="form-row">
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="name"
                                                        placeholder="Enter name"
                                                        required="required"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        name="email"
                                                        placeholder="Enter email"
                                                        required="required"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="form-group">
                          <textarea
                              name="message"
                              id="message"
                              className="form-control"
                              rows="7"
                              cols="25"
                              placeholder="Message"
                          ></textarea>
                                                </div>
                                            </div>
                                            <div className="col-sm-12 mt-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-brand-02"
                                                    id="btnContactUs"
                                                >
                                                    Send Message
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                            <div className="col-md-12 col-lg-6">
                                <div className="contact-us-content">
                                    <h2>Looking for a excellent Business idea?</h2>
                                    <p className="lead">
                                        Give us a call or drop by anytime, we endeavour to answer
                                        all enquiries within 24 hours on business days.
                                    </p>

                                    <a
                                        href="#"
                                        className="btn btn-outline-brand-01 align-items-center"
                                    >
                                        Get Directions <span className="ti-arrow-right pl-2"></span>
                                    </a>

                                    <hr className="my-5"/>

                                    <ul className="contact-info-list">
                                        <li className="d-flex pb-3">
                                            <div className="contact-icon mr-3">
                                                <span
                                                    className="fas fa-location-arrow color-primary rounded-circle p-3"></span>
                                            </div>
                                            <div className="contact-text">
                                                <h5 className="mb-1">Company Location</h5>
                                                <p>100 Yellow House, Mn Factory, United State, 13420</p>
                                            </div>
                                        </li>
                                        <li className="d-flex pb-3">
                                            <div className="contact-icon mr-3">
                                                <span
                                                    className="fas fa-envelope color-primary rounded-circle p-3"></span>
                                            </div>
                                            <div className="contact-text">
                                                <h5 className="mb-1">Email Address</h5>
                                                <p>hello@yourdomain.com</p>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section> */}
      </div>

      {/* <PricingPlans/> */}

      <Faqs />

      {/* <Newsletter/> */}
      <Footer />
    </>
  );
}

export default PharmacySoftware;
